import { action, computed, makeObservable, observable, reaction } from 'mobx';
import { ComparisonNodeBaseModel, SectionsNodeModel, LayersWithSectionsNodeModel } from '..';
import { NodeUtils } from '../../misc';
import { ContractComparisonPanel, SectionPair } from '../../types';
import SectionComparisonGroupModel from './SectionComparisonGroupModel';

export default class SectionsComparisonNodeModel extends ComparisonNodeBaseModel {
    private sectionGroups: SectionComparisonGroupModel[] = [];

    constructor(
        nodeId: string,
        nodeName: string,
        predefinedPanels: ContractComparisonPanel[] | undefined,
        readonly newNode: SectionsNodeModel | LayersWithSectionsNodeModel | undefined,
        readonly previousNode: SectionsNodeModel | LayersWithSectionsNodeModel | undefined,
        private readonly getSectionPairs: () => readonly SectionPair[],
        readonly layerId?: string
    ) {
        super(nodeId, nodeName, predefinedPanels);

        makeObservable<SectionsComparisonNodeModel, 'sectionGroups' | 'comparedSectionPairs'>(this, {
            inputIds: computed,
            newSections: computed,
            previousSections: computed,
            comparedSectionPairs: computed,
            sectionGroups: observable.ref,
            initializeGroups: action
        });

        this.initializeGroups();
        this.reactions.push(
            reaction(
                () => [this.comparedSectionPairs, this.panels, this.previousSections.map(section => section.isHidden)],
                () => this.initializeGroups()
            )
        );
    }

    private get comparedSectionPairs() {
        // Layout-hidden sections must not contribute trackers, counts or summary changes.
        // Observe visibility here so a rebuild can remove or restore the same section pair.
        return this.getSectionPairs().filter(pair => {
            const newSection = this.newSections.find(section => section.id === pair.newSection?.id);
            const previousSection = this.previousSections.find(section => section.id === pair.previousSection?.id);

            return !(newSection ?? previousSection)?.isHidden;
        });
    }

    get groups() {
        return this.sectionGroups;
    }

    get inputIds() {
        return this.getInputIds(
            this.newSections.flatMap(section => section.inputs.map(input => input.id)),
            this.previousSections.flatMap(section => section.inputs.map(input => input.id))
        );
    }

    get newSections() {
        return NodeUtils.getSections(this.newNode, this.layerId);
    }

    get previousSections() {
        return NodeUtils.getSections(this.previousNode, this.layerId);
    }

    dispose() {
        super.dispose();
        this.sectionGroups.forEach(group => group.dispose());
    }

    initializeGroups() {
        this.sectionGroups.forEach(group => group.dispose());
        this.sectionGroups = this.panels.flatMap(panel =>
            this.comparedSectionPairs.flatMap(pair => {
                const newSection = this.newSections.find(section => section.id === pair.newSection?.id);
                const previousSection = this.previousSections.find(section => section.id === pair.previousSection?.id);

                if (!newSection && !previousSection) {
                    return [];
                }

                const id = JSON.stringify([panel.id, pair.id]);

                const previousInputIds = previousSection?.isHidden
                    ? []
                    : previousSection?.inputs.map(input => input.id);

                const sectionInputIds = this.getInputIds(
                    newSection?.inputs.map(input => input.id),
                    previousInputIds
                );

                const inputIds = panel.inputIds.filter(inputId => sectionInputIds.includes(inputId));

                return new SectionComparisonGroupModel(id, panel.id, inputIds, pair, newSection, previousSection);
            })
        );
    }
}
